// AGC: WeatherHealthCard - Weather health widget with animated engineering graphs
// AGC: Shows precipitation, temperature, and radiation with time-based filtering
// AGC: Features: Time selector tabs, animated cumulative graphs, real weather data

import React, { useState, useMemo, useEffect, useRef } from 'react'
import { ChevronDown, ChevronUp, Droplets, Thermometer, Sun, AlertTriangle, Check, Info, CalendarDays } from 'lucide-react'
import { GlassCard } from '../shared'
import { useTranslation } from '../../hooks/useTranslation'
import { getHealthColor, type LotWeatherHealth, type DailyDataPoint } from '../../types'

// AGC: Time period options for filtering
type TimePeriod = 'today' | 'week' | 'month' | 'all'

interface WeatherHealthCardProps {
  weatherHealth: LotWeatherHealth
  onViewDetails: () => void
  safraStartDate?: string
  onStartSafra: () => void
  onStopSafra: () => void
}

/**
 * AGC: Circular progress ring component
 */
interface CircularRingProps {
  score: number
  size?: number
  strokeWidth?: number
  icon: React.ReactNode
  label: string
  sublabel: string
  iconColor: string
}

const CircularRing: React.FC<CircularRingProps> = ({
  score,
  size = 70,
  strokeWidth = 6,
  icon,
  label,
  sublabel,
  iconColor,
}) => {
  const { color } = getHealthColor(score)
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const progress = (score / 100) * circumference
  const offset = circumference - progress

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="transform -rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            className="text-gray-200 dark:text-gray-700"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className="transition-all duration-700 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-lg font-bold text-gray-900 dark:text-white">{score}</span>
        </div>
      </div>
      <div className={`mt-2 ${iconColor}`}>{icon}</div>
      <span className="text-xs font-medium text-gray-700 dark:text-gray-300 mt-1">{label}</span>
      <span className="text-xs text-gray-500 dark:text-gray-400">{sublabel}</span>
    </div>
  )
}

/**
 * AGC: Animated Area Chart Component
 * AGC: Shows cumulative data with smooth animation on mount and data change
 */
interface AnimatedAreaChartProps {
  data: DailyDataPoint[]
  title: string
  unit: string
  color: string
  showCumulative?: boolean
  showRequiredLine?: boolean
  animate: boolean
  height?: number
  labelCumulative?: string
  labelActual?: string
  labelRequired?: string
  labelNoData?: string
}

const AnimatedAreaChart: React.FC<AnimatedAreaChartProps> = ({
  data,
  title,
  unit,
  color,
  showCumulative = false,
  showRequiredLine = true,
  animate,
  height = 160,
  labelCumulative = 'Cumulative',
  labelActual = 'Actual',
  labelRequired = 'Required',
  labelNoData = 'No data available',
}) => {
  const [animationProgress, setAnimationProgress] = useState(0)
  const animationRef = useRef<number>()
  
  // AGC: Animate on mount or data change
  useEffect(() => {
    if (!animate) {
      setAnimationProgress(1)
      return
    }
    
    setAnimationProgress(0)
    const startTime = performance.now()
    const duration = 800 // AGC: Animation duration in ms
    
    const animateFrame = (currentTime: number) => {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1)
      // AGC: Easing function for smooth animation
      const eased = 1 - Math.pow(1 - progress, 3)
      setAnimationProgress(eased)
      
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animateFrame)
      }
    }
    
    animationRef.current = requestAnimationFrame(animateFrame)
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [data, animate])
  
  // AGC: Chart dimensions
  const width = 340
  const padding = { top: 25, right: 15, bottom: 30, left: 45 }
  const chartWidth = width - padding.left - padding.right
  const chartHeight = height - padding.top - padding.bottom
  
  // AGC: Process data for cumulative display
  const processedData = useMemo(() => {
    if (!data || data.length === 0) return null
    
    let cumulativeActual = 0
    let cumulativeRequired = 0
    
    const points = data.map((d, i) => {
      if (showCumulative) {
        cumulativeActual += d.actual
        cumulativeRequired += d.required
        return {
          ...d,
          displayActual: cumulativeActual,
          displayRequired: cumulativeRequired,
        }
      }
      return {
        ...d,
        displayActual: d.actual,
        displayRequired: d.required,
      }
    })
    
    const allValues = points.flatMap(p => [p.displayActual, p.displayRequired])
    const minVal = Math.min(...allValues) * 0.9
    const maxVal = Math.max(...allValues) * 1.1
    const range = maxVal - minVal || 1
    
    return { points, minVal, maxVal, range }
  }, [data, showCumulative])
  
  if (!processedData) {
    return (
      <div className="h-40 flex items-center justify-center text-gray-400 text-sm">
        {labelNoData}
      </div>
    )
  }
  
  const { points, minVal, maxVal, range } = processedData
  
  // AGC: Scale functions
  const xScale = (i: number) => padding.left + (i / Math.max(points.length - 1, 1)) * chartWidth
  const yScale = (v: number) => padding.top + chartHeight - ((v - minVal) / range) * chartHeight
  
  // AGC: Generate paths with animation
  const visiblePoints = Math.floor(points.length * animationProgress)
  const animatedPoints = points.slice(0, Math.max(visiblePoints, 1))
  
  // AGC: Actual values path
  const actualPath = animatedPoints
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(p.displayActual)}`)
    .join(' ')
  
  // AGC: Area path (filled)
  const areaPath = actualPath + 
    ` L ${xScale(animatedPoints.length - 1)} ${padding.top + chartHeight}` +
    ` L ${padding.left} ${padding.top + chartHeight} Z`
  
  // AGC: Required values path
  const requiredPath = animatedPoints
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(p.displayRequired)}`)
    .join(' ')
  
  // AGC: Grid lines
  const gridLines = []
  for (let i = 0; i <= 4; i++) {
    const y = padding.top + (i / 4) * chartHeight
    const value = maxVal - (i / 4) * range
    gridLines.push({ y, value: value.toFixed(0) })
  }
  
  // AGC: X-axis labels — show dd/mm dates instead of day numbers
  const xLabels = []
  const step = Math.max(1, Math.floor(points.length / 6))
  for (let i = 0; i < points.length; i += step) {
    const d = new Date(points[i].date)
    const label = `${d.getDate()}/${d.getMonth() + 1}`
    xLabels.push({ x: xScale(i), label, isForecast: points[i].isForecast })
  }
  
  const gradientId = `gradient-${title.replace(/\s/g, '-')}-${Date.now()}`
  
  return (
    <div className="relative">
      {/* AGC: Title and current value */}
      <div className="flex items-center justify-between mb-2 px-1">
        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{title}</span>
        <span className="text-xs font-mono bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded text-gray-600 dark:text-gray-400">
          {showCumulative
            ? points[points.length - 1]?.displayActual.toFixed(1)
            : points.reduce((sum, p) => sum + p.displayActual, 0).toFixed(1)
          } {unit}
        </span>
      </div>
      
      <svg 
        viewBox={`0 0 ${width} ${height}`}
        className="w-full"
        style={{ maxHeight: `${height}px` }}
      >
        {/* AGC: Gradient definition */}
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity="0.4" />
            <stop offset="100%" stopColor={color} stopOpacity="0.05" />
          </linearGradient>
        </defs>
        
        {/* AGC: Background */}
        <rect 
          x={padding.left} 
          y={padding.top} 
          width={chartWidth} 
          height={chartHeight}
          fill="rgba(248, 250, 252, 0.5)"
          className="dark:fill-gray-800/50"
          rx="4"
        />
        
        {/* AGC: Grid lines */}
        {gridLines.map((line, i) => (
          <g key={i}>
            <line
              x1={padding.left}
              y1={line.y}
              x2={padding.left + chartWidth}
              y2={line.y}
              stroke="rgba(156, 163, 175, 0.2)"
              strokeWidth="1"
              strokeDasharray={i === 0 || i === 4 ? "0" : "4,4"}
            />
            <text
              x={padding.left - 8}
              y={line.y + 4}
              textAnchor="end"
              className="text-[10px] fill-gray-500 dark:fill-gray-400 font-mono"
            >
              {line.value}
            </text>
          </g>
        ))}
        
        {/* AGC: Required line (dashed) */}
        {showRequiredLine && animationProgress > 0 && (
          <path
            d={requiredPath}
            fill="none"
            stroke="rgba(34, 197, 94, 0.7)"
            strokeWidth="2"
            strokeDasharray="6,3"
            className="transition-all duration-300"
          />
        )}
        
        {/* AGC: Actual area fill */}
        {animationProgress > 0 && (
          <path
            d={areaPath}
            fill={`url(#${gradientId})`}
            className="transition-all duration-300"
          />
        )}
        
        {/* AGC: Actual line */}
        {animationProgress > 0 && (
          <path
            d={actualPath}
            fill="none"
            stroke={color}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="transition-all duration-300"
          />
        )}
        
        {/* AGC: Data points */}
        {animatedPoints.map((p, i) => (
          <circle
            key={i}
            cx={xScale(i)}
            cy={yScale(p.displayActual)}
            r={i === animatedPoints.length - 1 ? 5 : 3}
            fill={p.isForecast ? 'rgba(156, 163, 175, 0.6)' : color}
            stroke={i === animatedPoints.length - 1 ? 'white' : 'none'}
            strokeWidth="2"
            className="transition-all duration-200"
          />
        ))}
        
        {/* AGC: X-axis labels */}
        {xLabels.map((label, i) => (
          <text
            key={i}
            x={label.x}
            y={height - 8}
            textAnchor="middle"
            className={`text-[9px] font-mono ${
              label.isForecast 
                ? 'fill-gray-400 dark:fill-gray-600' 
                : 'fill-gray-600 dark:fill-gray-400'
            }`}
          >
            {label.label}
          </text>
        ))}
        
        {/* AGC: Legend */}
        <g transform={`translate(${padding.left + 5}, ${padding.top - 8})`}>
          <circle cx="4" cy="0" r="3" fill={color} />
          <text x="12" y="3" className="text-[9px] fill-gray-600 dark:fill-gray-400">
            {showCumulative ? labelCumulative : labelActual}
          </text>
          {showRequiredLine && (
            <>
              <line x1="70" y1="0" x2="90" y2="0" stroke="rgba(34, 197, 94, 0.7)" strokeWidth="2" strokeDasharray="4,2" />
              <text x="95" y="3" className="text-[9px] fill-gray-600 dark:fill-gray-400">{labelRequired}</text>
            </>
          )}
        </g>
      </svg>
    </div>
  )
}

/**
 * AGC: Temperature Chart - Shows min/max/avg temperature
 */
interface TemperatureChartProps {
  data: DailyDataPoint[]
  optimalMin: number
  optimalMax: number
  animate: boolean
  height?: number
  title?: string
  labelOptimal?: string
  labelNoData?: string
}

const TemperatureChart: React.FC<TemperatureChartProps> = ({
  data,
  optimalMin,
  optimalMax,
  animate,
  height = 160,
  title: chartTitle = 'Temperature',
  labelOptimal = 'Optimal',
  labelNoData = 'No data',
}) => {
  const [animationProgress, setAnimationProgress] = useState(0)
  const animationRef = useRef<number>()
  
  useEffect(() => {
    if (!animate) {
      setAnimationProgress(1)
      return
    }
    
    setAnimationProgress(0)
    const startTime = performance.now()
    const duration = 800
    
    const animateFrame = (currentTime: number) => {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setAnimationProgress(eased)
      
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animateFrame)
      }
    }
    
    animationRef.current = requestAnimationFrame(animateFrame)
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [data, animate])
  
  const width = 340
  const padding = { top: 25, right: 15, bottom: 30, left: 45 }
  const chartWidth = width - padding.left - padding.right
  const chartHeight = height - padding.top - padding.bottom
  
  if (!data || data.length === 0) {
    return <div className="h-40 flex items-center justify-center text-gray-400">{labelNoData}</div>
  }
  
  const temps = data.map(d => d.actual)
  const minTemp = Math.min(...temps, optimalMin) - 3
  const maxTemp = Math.max(...temps, optimalMax) + 3
  const range = maxTemp - minTemp
  
  const xScale = (i: number) => padding.left + (i / Math.max(data.length - 1, 1)) * chartWidth
  const yScale = (v: number) => padding.top + chartHeight - ((v - minTemp) / range) * chartHeight
  
  const visiblePoints = Math.floor(data.length * animationProgress)
  const animatedData = data.slice(0, Math.max(visiblePoints, 1))
  
  const tempPath = animatedData
    .map((d, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(d.actual)}`)
    .join(' ')
  
  // AGC: Optimal temperature band
  const optimalBandY1 = yScale(optimalMax)
  const optimalBandY2 = yScale(optimalMin)
  
  // AGC: Grid lines
  const gridLines = []
  for (let i = 0; i <= 4; i++) {
    const y = padding.top + (i / 4) * chartHeight
    const value = maxTemp - (i / 4) * range
    gridLines.push({ y, value: value.toFixed(0) })
  }
  
  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-2 px-1">
        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{chartTitle}</span>
        <span className="text-xs font-mono bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded text-gray-600 dark:text-gray-400">
          {data[data.length - 1]?.actual.toFixed(1)}°C
        </span>
      </div>
      
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ maxHeight: `${height}px` }}>
        <defs>
          <linearGradient id="temp-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#ef4444" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#ef4444" stopOpacity="0.05" />
          </linearGradient>
        </defs>
        
        {/* AGC: Background */}
        <rect 
          x={padding.left} 
          y={padding.top} 
          width={chartWidth} 
          height={chartHeight}
          fill="rgba(248, 250, 252, 0.5)"
          className="dark:fill-gray-800/50"
          rx="4"
        />
        
        {/* AGC: Optimal temperature band */}
        <rect
          x={padding.left}
          y={optimalBandY1}
          width={chartWidth}
          height={optimalBandY2 - optimalBandY1}
          fill="rgba(34, 197, 94, 0.15)"
          stroke="rgba(34, 197, 94, 0.3)"
          strokeWidth="1"
          strokeDasharray="4,2"
        />
        <text
          x={padding.left + chartWidth - 5}
          y={optimalBandY1 + 12}
          textAnchor="end"
          className="text-[8px] fill-green-600 dark:fill-green-400"
        >
          {labelOptimal} {optimalMin}-{optimalMax}°C
        </text>
        
        {/* AGC: Grid lines */}
        {gridLines.map((line, i) => (
          <g key={i}>
            <line
              x1={padding.left}
              y1={line.y}
              x2={padding.left + chartWidth}
              y2={line.y}
              stroke="rgba(156, 163, 175, 0.2)"
              strokeWidth="1"
              strokeDasharray={i === 0 || i === 4 ? "0" : "4,4"}
            />
            <text
              x={padding.left - 8}
              y={line.y + 4}
              textAnchor="end"
              className="text-[10px] fill-gray-500 dark:fill-gray-400 font-mono"
            >
              {line.value}°
            </text>
          </g>
        ))}
        
        {/* AGC: Temperature area */}
        {animationProgress > 0 && (
          <path
            d={tempPath + ` L ${xScale(animatedData.length - 1)} ${padding.top + chartHeight} L ${padding.left} ${padding.top + chartHeight} Z`}
            fill="url(#temp-gradient)"
          />
        )}
        
        {/* AGC: Temperature line */}
        {animationProgress > 0 && (
          <path
            d={tempPath}
            fill="none"
            stroke="#ef4444"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
        
        {/* AGC: Data points with color based on optimal range */}
        {animatedData.map((d, i) => {
          const isOptimal = d.actual >= optimalMin && d.actual <= optimalMax
          const color = isOptimal ? '#22c55e' : d.actual > optimalMax ? '#ef4444' : '#3b82f6'
          return (
            <circle
              key={i}
              cx={xScale(i)}
              cy={yScale(d.actual)}
              r={i === animatedData.length - 1 ? 5 : 3}
              fill={d.isForecast ? 'rgba(156, 163, 175, 0.6)' : color}
              stroke={i === animatedData.length - 1 ? 'white' : 'none'}
              strokeWidth="2"
            />
          )
        })}
        
        {/* AGC: X-axis labels */}
        {data.filter((_, i) => i % Math.max(1, Math.floor(data.length / 6)) === 0).map((d, i, arr) => {
          const origIndex = data.indexOf(d)
          return (
            <text
              key={i}
              x={xScale(origIndex)}
              y={height - 8}
              textAnchor="middle"
              className={`text-[9px] font-mono ${d.isForecast ? 'fill-gray-400' : 'fill-gray-600 dark:fill-gray-400'}`}
            >
              D{origIndex + 1}
            </text>
          )
        })}
      </svg>
    </div>
  )
}

/**
 * AGC: Time Period Selector Tabs
 */
interface TimePeriodTabsProps {
  selected: TimePeriod
  onChange: (period: TimePeriod) => void
}

const TimePeriodTabs: React.FC<TimePeriodTabsProps> = ({ selected, onChange }) => {
  // AGC: Import translation hook for localized period labels
  const { t } = useTranslation()
  
  const tabs: { id: TimePeriod; label: string }[] = [
    { id: 'today', label: t('lotPage.periodTabs.today') },
    { id: 'week', label: t('lotPage.periodTabs.week') },
    { id: 'month', label: t('lotPage.periodTabs.month') },
    { id: 'all', label: t('lotPage.periodTabs.all') },
  ]
  
  return (
    <div className="flex bg-gray-100 dark:bg-gray-800 rounded-xl p-1 mb-4">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onChange(tab.id)}
          className={`flex-1 py-2 px-3 text-sm font-medium rounded-lg transition-all duration-300 ${
            selected === tab.id
              ? 'bg-white dark:bg-gray-700 text-[#0B5F4A] dark:text-emerald-400 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  )
}

/**
 * AGC: Main WeatherHealthCard Component
 */
export const WeatherHealthCard: React.FC<WeatherHealthCardProps> = ({
  weatherHealth,
  onViewDetails,
  safraStartDate,
  onStartSafra,
  onStopSafra,
}) => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('week')
  const [animateCharts, setAnimateCharts] = useState(false)
  const [weatherMode, setWeatherMode] = useState<'historico' | 'safra'>(
    safraStartDate ? 'safra' : 'historico'
  )

  const { scores, precipitation, temperature, radiation, daysSincePlanting } = weatherHealth
  const overallColor = getHealthColor(scores.overall)

  // AGC: Filter data based on selected period
  const filterDataByPeriod = (data: DailyDataPoint[]): DailyDataPoint[] => {
    if (!data || data.length === 0) return []
    
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    
    switch (selectedPeriod) {
      case 'today':
        return data.filter(d => {
          const date = new Date(d.date)
          return date.toDateString() === today.toDateString()
        }).slice(-1) // AGC: Just today's data point
      case 'week':
        const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
        return data.filter(d => new Date(d.date) >= weekAgo)
      case 'month':
        const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)
        return data.filter(d => new Date(d.date) >= monthAgo)
      case 'all':
      default:
        return data
    }
  }
  
  // AGC: Filtered data for charts
  const filteredPrecipitation = useMemo(() => 
    filterDataByPeriod(precipitation.daily), [precipitation.daily, selectedPeriod])
  const filteredTemperature = useMemo(() => 
    filterDataByPeriod(temperature.daily), [temperature.daily, selectedPeriod])
  const filteredRadiation = useMemo(() => 
    filterDataByPeriod(radiation.daily), [radiation.daily, selectedPeriod])
  
  // AGC: HISTÓRICO mode uses raw (non-cumulative) daily data
  const filteredRawPrecip = useMemo(() =>
    filterDataByPeriod(weatherHealth.rawPrecipDaily || []), [weatherHealth.rawPrecipDaily, selectedPeriod])
  const filteredRawTemp = useMemo(() =>
    filterDataByPeriod(weatherHealth.rawTempDaily || []), [weatherHealth.rawTempDaily, selectedPeriod])
  const filteredRawRadiation = useMemo(() =>
    filterDataByPeriod(weatherHealth.rawRadiationDaily || []), [weatherHealth.rawRadiationDaily, selectedPeriod])

  // AGC: Trigger animation when expanded or period changes
  useEffect(() => {
    if (isExpanded) {
      setAnimateCharts(false)
      const timer = setTimeout(() => setAnimateCharts(true), 50)
      return () => clearTimeout(timer)
    }
  }, [isExpanded, selectedPeriod])

  // AGC: Status indicators
  const formatDeficit = (value: number, unit: string): string => {
    if (value === 0) return `✓ OK`
    const sign = value < 0 ? '' : '+'
    return `${sign}${value.toFixed(0)} ${unit}`
  }

  const getPrecipStatus = () => {
    if (precipitation.deficit === 0) return { text: 'OK', color: 'text-green-600' }
    if (precipitation.deficit < 0) return { text: formatDeficit(precipitation.deficit, 'mm'), color: 'text-orange-600' }
    return { text: formatDeficit(precipitation.deficit, 'mm'), color: 'text-blue-600' }
  }

  const getTempStatus = () => {
    const issues = temperature.nightsAbove24 + temperature.nightsBelow10
    if (issues === 0) return { text: 'OK', color: 'text-green-600' }
    if (temperature.nightsAbove24 > 0) return { text: `${temperature.nightsAbove24} ${t('lotPage.hotNights')}`, color: 'text-orange-600' }
    return { text: `${temperature.nightsBelow10} ${t('lotPage.coldNights')}`, color: 'text-blue-600' }
  }

  const getRadiationStatus = () => {
    if (radiation.deficit === 0) return { text: 'OK', color: 'text-green-600' }
    return { text: formatDeficit(radiation.deficit, 'MJ'), color: 'text-orange-600' }
  }

  const precipStatus = getPrecipStatus()
  const tempStatus = getTempStatus()
  const radiationStatus = getRadiationStatus()

  // AGC: When no planting date, scores and "required" values are meaningless
  const hasPlantingDate = daysSincePlanting > 0

  return (
    <div className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
      {/* AGC: Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <Sun className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          </div>
          <h3 className="font-semibold text-gray-900 dark:text-white">
            {t('lotPage.weatherHealth')}
          </h3>
        </div>
        {hasPlantingDate ? (
          <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded-full">
            {daysSincePlanting} {t('lotPage.days')}
          </span>
        ) : (
          <span className="text-xs text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 rounded-full flex items-center gap-1">
            <CalendarDays className="w-3 h-3" />
            {t('lotPage.setPlantingDateHint')}
          </span>
        )}
      </div>

      {/* AGC: Circular rings — only show scores in SAFRA mode */}
      {weatherMode === 'safra' && safraStartDate && (
        <>
          <div className="flex justify-around items-start mb-6">
            <CircularRing
              score={scores.precipitation}
              icon={<Droplets className="w-5 h-5" />}
              label={t('lotPage.precipitation')}
              sublabel={precipStatus.text}
              iconColor={precipStatus.color}
            />
            <CircularRing
              score={scores.temperature}
              icon={<Thermometer className="w-5 h-5" />}
              label={t('lotPage.temperature')}
              sublabel={tempStatus.text}
              iconColor={tempStatus.color}
            />
            <CircularRing
              score={scores.radiation}
              icon={<Sun className="w-5 h-5" />}
              label={t('lotPage.radiation')}
              sublabel={radiationStatus.text}
              iconColor={radiationStatus.color}
            />
          </div>

          {/* AGC: Overall score bar */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('lotPage.overallScore')}
              </span>
              <span className={`text-lg font-bold ${overallColor.text}`}>
                {scores.overall}/100
              </span>
            </div>
            <div className="relative h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
            className="absolute inset-y-0 left-0 rounded-full transition-all duration-700"
            style={{ width: `${scores.overall}%`, backgroundColor: overallColor.color }}
          />
        </div>
          </div>
        </>
      )}

      {/* AGC: Expand toggle */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-center gap-1 mt-4 py-2 text-sm font-medium rounded-lg transition-colors"
        style={{ color: 'var(--accent-primary)' }}
      >
        <span>{isExpanded ? t('lotPage.hideDetails') : t('lotPage.viewDetails')}</span>
        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {/* AGC: Expanded content with animated charts */}
      {isExpanded && (
        <div className="mt-4 space-y-6 border-t border-gray-200 dark:border-gray-700 pt-4">
          {/* AGC: Mode toggle — HISTÓRICO / SAFRA */}
          <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden mb-3">
            <button
              type="button"
              onClick={() => setWeatherMode('historico')}
              className={`flex-1 py-2 text-sm font-semibold transition-colors ${
                weatherMode === 'historico'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
              }`}
            >
              {t('lotPage.modeHistorico')}
            </button>
            <button
              type="button"
              onClick={() => {
                if (!safraStartDate) {
                  onStartSafra()
                } else {
                  setWeatherMode('safra')
                }
              }}
              className={`flex-1 py-2 text-sm font-semibold transition-colors border-l border-gray-300 dark:border-gray-600 ${
                weatherMode === 'safra'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
              }`}
            >
              {t('lotPage.modeSafra')}
            </button>
          </div>

          {/* AGC: SAFRA info banner when active */}
          {weatherMode === 'safra' && safraStartDate && (
            <div className="flex items-center justify-between bg-emerald-50 dark:bg-emerald-900/20 rounded-lg px-3 py-2 mb-3">
              <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400">
                {t('lotPage.safraActive')} — {t('lotPage.safraSince')} {new Date(safraStartDate).toLocaleDateString()}
              </span>
              <button
                type="button"
                onClick={onStopSafra}
                className="text-xs text-red-600 dark:text-red-400 font-medium hover:underline"
              >
                {t('lotPage.stopSafra')}
              </button>
            </div>
          )}

          {/* AGC: Time Period Selector */}
          <TimePeriodTabs
            selected={selectedPeriod} 
            onChange={(period) => {
              setSelectedPeriod(period)
            }} 
          />
          
          {/* AGC: HISTÓRICO mode — raw daily data, no required line */}
          {weatherMode === 'historico' && (
            <>
              <div className="bg-blue-50/50 dark:bg-blue-900/10 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Droplets className="w-4 h-4 text-blue-500" />
                  <span className="text-xs text-gray-500">{t('lotPage.precipitation')} (mm/{t('lotPage.days')})</span>
                </div>
                <AnimatedAreaChart
                  data={filteredRawPrecip}
                  title={t('lotPage.precipitation')}
                  unit="mm"
                  color="#3b82f6"
                  showCumulative={false}
                  showRequiredLine={false}
                  animate={animateCharts}
                  labelCumulative={t('lotPage.accumulated')}
                  labelActual={t('lotPage.accumulated')}
                  labelRequired={t('lotPage.required')}
                  labelNoData={t('lotPage.noData')}
                />
              </div>

              <div className="bg-red-50/50 dark:bg-red-900/10 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Thermometer className="w-4 h-4 text-red-500" />
                  <span className="text-xs text-gray-500">
                    {t('lotPage.avgReal')}: {temperature.avgReal.toFixed(1)}°C
                  </span>
                </div>
                <TemperatureChart
                  data={filteredRawTemp}
                  optimalMin={temperature.optimalMin}
                  optimalMax={temperature.optimalMax}
                  animate={animateCharts}
                  title={t('lotPage.temperature')}
                  labelOptimal={t('lotPage.optimal')}
                  labelNoData={t('lotPage.noData')}
                />
              </div>

              <div className="bg-amber-50/50 dark:bg-amber-900/10 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Sun className="w-4 h-4 text-amber-500" />
                  <span className="text-xs text-gray-500">{t('lotPage.radiation')} (MJ/m²/{t('lotPage.days')})</span>
                </div>
                <AnimatedAreaChart
                  data={filteredRawRadiation}
                  title={t('lotPage.radiation')}
                  unit="MJ/m²"
                  color="#f59e0b"
                  showCumulative={false}
                  showRequiredLine={false}
                  animate={animateCharts}
                  labelCumulative={t('lotPage.accumulated')}
                  labelActual={t('lotPage.accumulated')}
                  labelRequired={t('lotPage.required')}
                  labelNoData={t('lotPage.noData')}
                />
              </div>
            </>
          )}

          {/* AGC: SAFRA mode — cumulative data with required line and info icons */}
          {weatherMode === 'safra' && safraStartDate && (
            <>
              <div className="bg-blue-50/50 dark:bg-blue-900/10 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Droplets className="w-4 h-4 text-blue-500" />
                  <span className="text-xs text-gray-500">
                    {t('lotPage.accumulated')}: {precipitation.accumulated.toFixed(0)} mm
                  </span>
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    | {precipitation.required.toFixed(0)} mm {t('lotPage.required')}
                    <button type="button" onClick={() => alert(t('lotPage.requiredInfoPrecip'))} className="text-gray-400 hover:text-gray-600">
                      <Info className="w-3 h-3" />
                    </button>
                  </span>
                </div>
                <AnimatedAreaChart
                  data={filteredPrecipitation}
                  title={t('lotPage.precipitation')}
                  unit="mm"
                  color="#3b82f6"
                  showCumulative={false}
                  showRequiredLine={true}
                  animate={animateCharts}
                  labelCumulative={t('lotPage.accumulated')}
                  labelActual={t('lotPage.accumulated')}
                  labelRequired={t('lotPage.required')}
                  labelNoData={t('lotPage.noData')}
                />
              </div>

              <div className="bg-red-50/50 dark:bg-red-900/10 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Thermometer className="w-4 h-4 text-red-500" />
                  <span className="text-xs text-gray-500">
                    {t('lotPage.avgReal')}: {temperature.avgReal.toFixed(1)}°C | {t('lotPage.optimal')}: {temperature.optimalMin}-{temperature.optimalMax}°C
                  </span>
                </div>
                <TemperatureChart
                  data={filteredTemperature}
                  optimalMin={temperature.optimalMin}
                  optimalMax={temperature.optimalMax}
                  animate={animateCharts}
                  title={t('lotPage.temperature')}
                  labelOptimal={t('lotPage.optimal')}
                  labelNoData={t('lotPage.noData')}
                />
              </div>

              <div className="bg-amber-50/50 dark:bg-amber-900/10 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Sun className="w-4 h-4 text-amber-500" />
                  <span className="text-xs text-gray-500">
                    {t('lotPage.accumulated')}: {radiation.accumulated.toFixed(0)} MJ/m²
                  </span>
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    | {radiation.required.toFixed(0)} MJ/m² {t('lotPage.required')}
                    <button type="button" onClick={() => alert(t('lotPage.requiredInfoRadiation'))} className="text-gray-400 hover:text-gray-600">
                      <Info className="w-3 h-3" />
                    </button>
                  </span>
                </div>
                <AnimatedAreaChart
                  data={filteredRadiation}
                  title={t('lotPage.radiation')}
                  unit="MJ/m²"
                  color="#f59e0b"
                  showCumulative={false}
                  showRequiredLine={true}
                  animate={animateCharts}
                  labelCumulative={t('lotPage.accumulated')}
                  labelActual={t('lotPage.accumulated')}
                  labelRequired={t('lotPage.required')}
                  labelNoData={t('lotPage.noData')}
                />
              </div>
            </>
          )}

          {/* AGC: View full details button */}
          <button
            onClick={onViewDetails}
            className="w-full py-2.5 text-sm font-medium rounded-lg transition-colors border"
            style={{ 
              color: 'var(--accent-primary)', 
              borderColor: 'var(--accent-primary)' 
            }}
          >
            {t('lotPage.viewFullDetails')} →
          </button>
        </div>
      )}
    </div>
  )
}

export default WeatherHealthCard
